import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, ChevronDown } from "lucide-react";
import Breadcrumbs, { useCanonical } from "./Breadcrumbs";

const navigation = [
  { name: "Home", href: "/" },
  { 
    name: "Services", 
    href: "/services",
    children: [
      { name: "Scaffolding Rental", href: "/scaffolding-rental" },
      { name: "Shuttering Material", href: "/shuttering-material-on-rent" },
      { name: "Cuplock Scaffolding", href: "/cuplock-scaffolding" },
      { name: "Acrospan Props", href: "/acrow-span-props" },
      { name: "Industrial Scaffolding", href: "/industrial-scaffolding" },
    ]
  },
  { 
    name: "Locations", 
    href: "#",
    children: [
      { name: "Noida", href: "/scaffolding-rental-noida" },
      { name: "Delhi NCR", href: "/scaffolding-rental-delhi-ncr" },
      { name: "Hyderabad", href: "/scaffolding-rental-hyderabad" },
      { name: "Vizag", href: "/scaffolding-rental-vizag" },
    ]
  },
  { name: "Projects", href: "/projects" },
  { name: "Gallery", href: "/gallery" },
  { name: "Testimonials", href: "/testimonials" },
  { name: "About", href: "/about" },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  return (
    <>
      {/* Top Info Bar - Phone & Email on left, Locations on right */}
      <div className="bg-slate-900 text-white py-2 px-4 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center space-x-5">
            <a href="tel:+919355744699" className="flex items-center space-x-1.5 text-slate-300 hover:text-yellow-400 transition-colors" data-testid="topbar-phone">
              <Phone className="w-3.5 h-3.5 text-yellow-500" />
              <span>+91 9355744699</span>
            </a>
            <a href="mailto:info@amarpreet.in" className="hidden sm:flex items-center space-x-1.5 text-slate-300 hover:text-yellow-400 transition-colors" data-testid="topbar-email">
              <Mail className="w-3.5 h-3.5 text-yellow-500" />
              <span>info@amarpreet.in</span>
            </a>
          </div>
          <div className="flex items-center space-x-1.5 text-slate-400">
            <MapPin className="w-3.5 h-3.5 text-yellow-500" />
            <span className="hidden md:inline">Delhi | Noida | Hyderabad | Vizag</span>
            <span className="md:hidden">Delhi | Noida | Hyd | Vizag</span>
          </div>
        </div>
      </div>

      {/* Main Navigation - White like old website */}
      <header className={`sticky top-0 z-50 transition-all duration-300 bg-white ${
        scrolled ? "shadow-lg" : "shadow-sm"
      }`}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Enlarged like old website */}
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
              <img 
                src="/images/aps-logo.png" 
                alt="APS Logo" 
                className="h-16 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='64' viewBox='0 0 120 64'%3E%3Crect fill='%23eab308' width='120' height='64' rx='4'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%230f172a' font-size='24' font-weight='bold'%3EAPS%3C/text%3E%3C/svg%3E";
                }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => (
                <div key={item.name} className="relative group">
                  {item.children ? (
                    <button
                      className="flex items-center px-3 py-2 text-slate-700 hover:text-yellow-600 transition-colors font-medium text-sm"
                      onMouseEnter={() => setActiveDropdown(item.name)}
                      onMouseLeave={() => setActiveDropdown(null)}
                      data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {item.name}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className={`px-3 py-2 font-medium text-sm transition-colors ${
                        location.pathname === item.href 
                          ? "text-yellow-600" 
                          : "text-slate-700 hover:text-yellow-600"
                      }`}
                      data-testid={`nav-${item.name.toLowerCase()}`}
                    >
                      {item.name}
                    </Link>
                  )}
                  
                  {item.children && (
                    <div 
                      className={`absolute top-full left-0 w-56 bg-white shadow-xl rounded-lg py-2 border border-slate-100 transition-all duration-200 ${
                        activeDropdown === item.name ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
                      }`}
                      onMouseEnter={() => setActiveDropdown(item.name)}
                      onMouseLeave={() => setActiveDropdown(null)}
                    >
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className="block px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-yellow-600 transition-colors"
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Call Now + WhatsApp Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <a
                href="tel:+919355744699"
                className="flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-4 py-2 rounded-full font-semibold text-sm transition-all hover:scale-105"
                data-testid="nav-call-btn"
              >
                <Phone className="w-4 h-4" />
                <span>Call Now</span>
              </a>
              <a
                href="https://wa.me/919355744699?text=Hi%2C%20I%27m%20interested%20in%20scaffolding%20rental%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full font-semibold text-sm transition-all hover:scale-105"
                data-testid="nav-whatsapp-btn"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span>WhatsApp</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-slate-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 bg-white border-t border-slate-100 rounded-b-lg">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.children ? (
                    <div className="py-2">
                      <span className="font-medium text-slate-700 px-4 text-sm">{item.name}</span>
                      <div className="mt-2 pl-4 space-y-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className="block py-2 px-4 text-sm text-slate-500 hover:text-yellow-600"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className="block py-2 px-4 font-medium text-slate-700 hover:text-yellow-600 text-sm"
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4 px-4 flex gap-3">
                <a
                  href="tel:+919355744699"
                  className="flex-1 flex items-center justify-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-4 py-3 rounded-lg font-semibold text-sm"
                >
                  <Phone className="w-4 h-4" />
                  <span>Call Now</span>
                </a>
                <a
                  href="https://wa.me/919355744699?text=Hi%2C%20I%27m%20interested%20in%20scaffolding%20rental%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-semibold text-sm"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          )}
        </nav>
      </header>
    </>
  );
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info with White Logo */}
          <div>
            <img 
              src="/images/aps-logo.png" 
              alt="Amar Preet Scaffolding" 
              className="h-12 w-auto mb-4 brightness-0 invert"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
              }}
            />
            <p className="text-slate-400 mb-6 leading-relaxed text-sm">
              30+ years of trusted scaffolding and shuttering solutions for India's leading builders, contractors, and EPC companies.
            </p>
            <div className="flex items-center space-x-4">
              <span className="bg-yellow-500/20 text-yellow-400 text-xs font-bold px-3 py-1 rounded-full">
                ISO 9001:2015
              </span>
              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/company/amar-preet-shuttering-scaffolding/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-slate-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                data-testid="footer-linkedin"
              >
                <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-6">Services</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/scaffolding-rental" className="text-slate-400 hover:text-yellow-400 transition-colors">Scaffolding Rental</Link></li>
              <li><Link to="/shuttering-material-on-rent" className="text-slate-400 hover:text-yellow-400 transition-colors">Shuttering Material</Link></li>
              <li><Link to="/cuplock-scaffolding" className="text-slate-400 hover:text-yellow-400 transition-colors">Cuplock Scaffolding</Link></li>
              <li><Link to="/acrow-span-props" className="text-slate-400 hover:text-yellow-400 transition-colors">Acrospan Props</Link></li>
              <li><Link to="/industrial-scaffolding" className="text-slate-400 hover:text-yellow-400 transition-colors">Industrial Scaffolding</Link></li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/scaffolding-rental-noida" className="text-slate-400 hover:text-yellow-400 transition-colors">Scaffolding Noida</Link></li>
              <li><Link to="/scaffolding-rental-delhi-ncr" className="text-slate-400 hover:text-yellow-400 transition-colors">Scaffolding Delhi NCR</Link></li>
              <li><Link to="/scaffolding-rental-hyderabad" className="text-slate-400 hover:text-yellow-400 transition-colors">Scaffolding Hyderabad</Link></li>
              <li><Link to="/scaffolding-rental-vizag" className="text-slate-400 hover:text-yellow-400 transition-colors">Scaffolding Vizag</Link></li>
              <li><Link to="/gallery" className="text-slate-400 hover:text-yellow-400 transition-colors">Gallery</Link></li>
              <li><Link to="/testimonials" className="text-slate-400 hover:text-yellow-400 transition-colors">Testimonials</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a href="tel:+919355744699" className="flex items-center space-x-3 text-slate-400 hover:text-yellow-400 transition-colors">
                  <Phone className="w-5 h-5 text-yellow-500" />
                  <span>+91 9355744699</span>
                </a>
              </li>
              <li>
                <a href="mailto:info@amarpreet.in" className="flex items-center space-x-3 text-slate-400 hover:text-yellow-400 transition-colors">
                  <Mail className="w-5 h-5 text-yellow-500" />
                  <span>info@amarpreet.in</span>
                </a>
              </li>
              <li className="flex items-start space-x-3 text-slate-400">
                <MapPin className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-1" />
                <span>Delhi | Noida | Hyderabad | Vizag</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-slate-500 text-sm">
              © {currentYear} Amar Preet Scaffolding. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <Link to="/about" className="text-slate-500 hover:text-yellow-400 transition-colors">About Us</Link>
              <Link to="/projects" className="text-slate-500 hover:text-yellow-400 transition-colors">Projects</Link>
              <Link to="/contact" className="text-slate-500 hover:text-yellow-400 transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Button Component
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/919355744699?text=Hi%2C%20I%27m%20interested%20in%20scaffolding%20rental%20services."
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-[99999] group"
      data-testid="whatsapp-button"
    >
      <div className="relative">
        {/* Notification badge */}
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold z-10 animate-pulse">
          1
        </span>
        {/* Ping animation ring */}
        <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-30" />
        {/* Button */}
        <div className="relative w-14 h-14 bg-green-500 group-hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-all">
          <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </div>
      </div>
      {/* Tooltip */}
      <div className="absolute bottom-16 right-0 bg-white text-slate-800 text-sm font-medium px-4 py-2 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-slate-100">
        Chat with us on WhatsApp
        <div className="absolute -bottom-1 right-6 w-2 h-2 bg-white border-r border-b border-slate-100 rotate-45" />
      </div>
    </a>
  );
};

const Layout = () => {
  useCanonical();
  const location = useLocation();
  const isHome = location.pathname === "/";

  return (
    <div className="App min-h-screen">
      <Header />
      {!isHome && <Breadcrumbs />}
      <main>
        <Outlet />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Layout;
